import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Col, Modal, Row, Space, Statistic, Table, Tag, Typography, message } from 'antd';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import pmvService, { type PmvMatchCriteriaResult, type PmvMatchResult } from '@/services/pmv.service';
import { useProjects } from '@/hooks/useProjects';
import { buildProjectIdToLabelMap, resolveProjectDisplayFromMap } from '@/utils/pmvDisplay';

const { Title, Text } = Typography;

const FULFILMENT_STAGES = [
  { title: 'Draft' },
  { title: 'Submitted' },
  { title: 'Matching' },
  { title: 'Reserved/Allocated' },
  { title: 'Closed' },
];

function fulfilmentStageIndex(status?: string, hasMatches?: boolean): number {
  const s = String(status || '').toUpperCase();
  if (!s || s === 'DRAFT') return 0;
  if (s === 'FULFILLED' || s === 'CLOSED' || s === 'CANCELLED') return 4;
  if (s === 'PARTIALLY_FULFILLED') return 3;
  if (s === 'SUBMITTED') return hasMatches ? 2 : 1;
  return 1;
}

function conflictErrorMessage(e: unknown, fallback: string): string {
  const err = e as {
    message?: string;
    response?: { status?: number; data?: { message?: string } };
  };
  const raw = String(err?.response?.data?.message || err?.message || '');
  const status = err?.response?.status;
  if (status === 409 || /conflict|409|overlap/i.test(raw)) {
    return 'This unit was reserved by another user for the selected period.';
  }
  return raw || fallback;
}

function formatCriterionValue(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function criteriaPlainLanguage(c: PmvMatchCriteriaResult): string {
  const label = c.label || c.attribute_code || 'Criterion';
  const required = formatCriterionValue(c.required);
  const actual = formatCriterionValue(c.actual);
  const op = c.operator ? ` (${c.operator})` : '';
  if (c.pass === true) {
    return `${label}${op}: required ${required}, actual ${actual} — PASS`;
  }
  if (c.pass === false) {
    const why = c.message ? ` (${c.message})` : '';
    return `${label}${op}: required ${required}, actual ${actual} — FAIL${why}`;
  }
  return `${label}${op}: required ${required}, actual ${actual}`;
}

const PmvRequestWorkspacePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: projects = [] } = useProjects();
  const projectLabels = useMemo(() => buildProjectIdToLabelMap(projects), [projects]);
  const [sessionReservations, setSessionReservations] = useState<Record<string, string>>({});

  const reqQ = useQuery({
    queryKey: ['pmv-request', id],
    queryFn: () => pmvService.getRequest(id!),
    enabled: Boolean(id),
  });

  const classQ = useQuery({
    queryKey: ['pmv-classifications'],
    queryFn: () => pmvService.listClassifications(),
  });

  const matchesQ = useQuery({
    queryKey: ['pmv-request-matches', id],
    queryFn: () => pmvService.getRequestMatches(id!),
    enabled: Boolean(id) && reqQ.data?.status !== 'DRAFT',
  });

  const submitMut = useMutation({
    mutationFn: () => pmvService.submitRequest(id!),
    onSuccess: () => {
      message.success('Equipment request submitted');
      void qc.invalidateQueries({ queryKey: ['pmv-request', id] });
      void qc.invalidateQueries({ queryKey: ['pmv-request-matches', id] });
    },
    onError: (e: Error) => message.error(e.message || 'Submit failed'),
  });

  const reserveMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => pmvService.createReservation(body),
    onSuccess: (reservation) => {
      message.success('Reserved');
      if (reservation?.id && reservation.pmv_profile_id) {
        setSessionReservations((prev) => ({ ...prev, [reservation.pmv_profile_id]: reservation.id }));
      }
      void qc.invalidateQueries({ queryKey: ['pmv-request-matches', id] });
    },
    onError: (e: Error) => message.error(conflictErrorMessage(e, 'Reservation failed')),
  });

  const cancelReserveMut = useMutation({
    mutationFn: (reservationId: string) => pmvService.cancelReservation(reservationId),
    onSuccess: (_data, reservationId) => {
      message.success('Reservation cancelled');
      setSessionReservations((prev) => {
        const next = { ...prev };
        for (const [profileId, rid] of Object.entries(next)) {
          if (rid === reservationId) delete next[profileId];
        }
        return next;
      });
      void qc.invalidateQueries({ queryKey: ['pmv-request-matches', id] });
    },
    onError: (e: Error) => message.error(e.message || 'Cancel failed'),
  });

  const allocateMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => pmvService.createAllocation(body),
    onSuccess: (alloc) => {
      message.success('Allocated');
      void qc.invalidateQueries({ queryKey: ['pmv-request', id] });
      navigate(`/asset/pmv/allocations/${alloc.id}`);
    },
    onError: (e: Error) => message.error(conflictErrorMessage(e, 'Allocation failed')),
  });

  const req = reqQ.data;
  const fulfilled = req?.requirements?.reduce((s, r) => s + r.quantity_fulfilled, 0) ?? 0;
  const required = req?.requirements?.reduce((s, r) => s + r.quantity_required, 0) ?? 0;
  const remaining = Math.max(0, required - fulfilled);
  const hasMatches = (matchesQ.data?.length ?? 0) > 0;
  const stageCurrent = fulfilmentStageIndex(req?.status, hasMatches);

  const classLabel = (classificationId: string) => {
    const c = (classQ.data ?? []).find((x) => x.id === classificationId);
    return c ? `${c.code} — ${c.name}` : '—';
  };

  const projectLabel = req ? resolveProjectDisplayFromMap(projectLabels, req.project_id) : '';

  const primaryActions =
    req?.status === 'DRAFT' ? (
      <Space wrap>
        <Button type="primary" loading={submitMut.isPending} onClick={() => submitMut.mutate()}>
          Submit draft
        </Button>
        <Button onClick={() => navigate(`/asset/pmv/requests/${req.id}/edit`)}>Edit draft</Button>
      </Space>
    ) : undefined;

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Equipment Request"
          documentNumber={req?.request_number ?? ''}
          statusTag={req ? <Tag>{req.status}</Tag> : undefined}
          subtitle={
            req ? (
              <Text type="secondary">
                Project: <strong>{projectLabel}</strong>
              </Text>
            ) : undefined
          }
          primaryActions={primaryActions}
        />
      }
      workflow={
        req ? (
          <WorkflowStrip compact current={stageCurrent} stages={FULFILMENT_STAGES} />
        ) : undefined
      }
    >
      {req && (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card size="small" title="Fulfilment progress">
            <Row gutter={[16, 16]}>
              <Col xs={8}>
                <Statistic title="Required" value={required} />
              </Col>
              <Col xs={8}>
                <Statistic title="Fulfilled" value={fulfilled} />
              </Col>
              <Col xs={8}>
                <Statistic title="Remaining" value={remaining} />
              </Col>
            </Row>
          </Card>

          <Title level={5}>Requirements</Title>
          <Table
            rowKey="id"
            dataSource={req.requirements ?? []}
            pagination={false}
            columns={[
              {
                title: 'Classification',
                dataIndex: 'classification_id',
                render: (cid: string) => classLabel(cid),
              },
              { title: 'Qty required', dataIndex: 'quantity_required' },
              { title: 'Fulfilled', dataIndex: 'quantity_fulfilled' },
              { title: 'From', dataIndex: 'required_from' },
              { title: 'Until', dataIndex: 'required_until' },
            ]}
          />

          {req.status !== 'DRAFT' &&
            matchesQ.data?.map((block) => (
              <Card key={block.requirement_id} title="Matching units" size="small">
                <Table
                  rowKey="profile_id"
                  dataSource={block.matches}
                  pagination={false}
                  expandable={{
                    rowExpandable: (m: PmvMatchResult) =>
                      Array.isArray(m.criteria_results) && m.criteria_results.length > 0,
                    expandedRowRender: (m: PmvMatchResult) => (
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Text type="secondary">Match criteria</Text>
                        {(m.criteria_results ?? []).map((c, i) => (
                          <div key={`${m.profile_id}-c-${i}`}>
                            <Tag color={c.pass === true ? 'success' : c.pass === false ? 'error' : 'default'}>
                              {c.pass === true ? 'PASS' : c.pass === false ? 'FAIL' : '—'}
                            </Tag>{' '}
                            <Text>{criteriaPlainLanguage(c)}</Text>
                          </div>
                        ))}
                      </Space>
                    ),
                  }}
                  columns={[
                    { title: 'Unit', dataIndex: 'asset_code' },
                    { title: 'Name', dataIndex: 'asset_name' },
                    {
                      title: 'Match',
                      dataIndex: 'match_status',
                      render: (v: string, m: PmvMatchResult) => (
                        <Space size={4} wrap>
                          <Tag color={v === 'FULL_MATCH' ? 'success' : 'default'}>{v}</Tag>
                          {m.criteria_results?.length ? (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {m.criteria_results.filter((c) => c.pass).length}/
                              {m.criteria_results.length} criteria
                            </Text>
                          ) : null}
                        </Space>
                      ),
                    },
                    { title: 'Status', dataIndex: 'operational_status' },
                    {
                      title: 'Actions',
                      render: (_: unknown, m: PmvMatchResult) =>
                        m.match_status === 'FULL_MATCH' && req ? (
                          <Space wrap>
                            {sessionReservations[m.profile_id] ? (
                              <Button
                                size="small"
                                danger
                                loading={cancelReserveMut.isPending}
                                onClick={() => {
                                  const reservationId = sessionReservations[m.profile_id];
                                  Modal.confirm({
                                    title: 'Cancel reservation?',
                                    content: `Release hold on ${m.asset_code}.`,
                                    okText: 'Cancel reservation',
                                    okButtonProps: { danger: true },
                                    onOk: () => cancelReserveMut.mutateAsync(reservationId),
                                  });
                                }}
                              >
                                Cancel reservation
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                loading={reserveMut.isPending}
                                onClick={() => {
                                  const line = req.requirements.find((r) => r.id === block.requirement_id);
                                  if (!line) return;
                                  reserveMut.mutate({
                                    pmv_profile_id: m.profile_id,
                                    request_id: req.id,
                                    requirement_id: line.id,
                                    project_id: req.project_id,
                                    from_date: line.required_from,
                                    until_date: line.required_until,
                                  });
                                }}
                              >
                                Reserve
                              </Button>
                            )}
                            <Button
                              size="small"
                              type="primary"
                              loading={allocateMut.isPending}
                              onClick={() => {
                                const line = req.requirements.find((r) => r.id === block.requirement_id);
                                if (!line) return;
                                allocateMut.mutate({
                                  pmv_profile_id: m.profile_id,
                                  request_id: req.id,
                                  requirement_id: line.id,
                                  project_id: req.project_id,
                                  planned_start: line.required_from,
                                  planned_end: line.required_until,
                                });
                              }}
                            >
                              Allocate
                            </Button>
                          </Space>
                        ) : null,
                    },
                  ]}
                />
              </Card>
            ))}

          <Space wrap>
            <Button onClick={() => navigate('/asset/pmv/requests')}>Back to Equipment Requests</Button>
            <Button onClick={() => navigate('/asset/pmv/operations')}>Deployment Control</Button>
          </Space>
        </Space>
      )}
    </WorkspaceLayout>
  );
};

export default PmvRequestWorkspacePage;
